// frontend/src/pages/p2p/market/MarketList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import p2pAxios from '../../../p2pAxios';

const FEE_RATE = 0.005;   // 0.5%
const GST_RATE = 0.18;    // 18%
const TDS_RATE = 0.01;    // 1%

const COIN_DECIMALS = { USDT: 6, USDC: 6, BTC: 8, ETH: 8 };

// ---------- number helpers ----------
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const r2 = (n) => +Number(n || 0).toFixed(2);
const rN = (n, d) => +Number(n || 0).toFixed(d);
const fmtINR = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtCoin = (n, dp = 6) =>
  Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: dp,
  });

// ---- expiry helpers ----
const parseExpireMinutes = (v, fallback = 45) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return clamp(Math.round(n), 15, 180);
};
const fmtExpiry = (m) => {
  const mins = parseExpireMinutes(m);
  if (mins % 60 === 0) return `${mins / 60}h`;
  if (mins > 60) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${mins} min`;
};

// ---------- presence helpers ----------
function formatLastSeen(dt) {
  if (!dt) return "Last seen —";
  const diffMs = Date.now() - new Date(dt).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Last seen just now";
  if (mins < 60) return `Last seen ${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Last seen ${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `Last seen ${days}d ago`;
}

// ✅ NEW: presence label helper (uses backend presence.status when available)
function getPresenceLabel(presence) {
  const status = String(presence?.status || '').toLowerCase();
  const lastSeenAt = presence?.lastSeenAt || null;
  const online = !!presence?.online;

  if (status === 'online' || online) return "Online";
  if (status === 'last_seen') return formatLastSeen(lastSeenAt);

  // fallback (older backend): if lastSeenAt exists, show last seen, else Offline
  if (lastSeenAt) return formatLastSeen(lastSeenAt);

  return "Offline";
}

// ✅ NEW: presence style helper
function getPresenceStyle(presence) {
  const status = String(presence?.status || '').toLowerCase();
  const online = !!presence?.online;
  const effectiveOnline = status === 'online' || online;

  if (effectiveOnline) {
    return {
      wrapper:
        "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
      dot: "bg-green-500",
    };
  }

  // show "last seen" as neutral (not "offline red")
  if (status === 'last_seen' || presence?.lastSeenAt) {
    return {
      wrapper:
        "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
      dot: "bg-orange-500",
    };
  }

  // truly offline
  return {
    wrapper:
      "bg-gray-50 text-gray-700 border-gray-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700",
    dot: "bg-gray-400",
  };
}

// ---------- fees breakdown ----------
// Buyer: platform fee + GST (no TDS)
// Seller: platform fee + GST + TDS(1% INR)
function computeBreakdown(fiatINR, price, isSeller, symbol = 'USDT') {
  if (!fiatINR || !price || fiatINR <= 0) return null;

  const dp = COIN_DECIMALS[symbol] ?? 8;
  const grossInr = r2(fiatINR);
  const grossCoin = rN(grossInr / price, dp);

  const tdsInr = isSeller ? r2(grossInr * TDS_RATE) : 0;
  const tdsCoin = isSeller ? rN(tdsInr / price, dp) : 0;

  const payableFiatInr = isSeller ? r2(grossInr - tdsInr) : grossInr;

  const sellerPlatformFeeCoin = isSeller ? rN(grossCoin * FEE_RATE, dp) : 0;
  const sellerGstCoin = isSeller ? rN(sellerPlatformFeeCoin * GST_RATE, dp) : 0;
  const sellerTotalDeductedCoin = isSeller
    ? rN(grossCoin + sellerPlatformFeeCoin + sellerGstCoin, dp)
    : grossCoin;

  const buyerPlatformFeeCoin = rN(grossCoin * FEE_RATE, dp);
  const buyerGstCoin = rN(buyerPlatformFeeCoin * GST_RATE, dp);
  const buyerNetReceiveCoin = rN(grossCoin - buyerPlatformFeeCoin - buyerGstCoin, dp);

  return {
    grossCoin,
    tdsInr,
    tdsCoin,
    payableFiatInr,
    sellerPlatformFeeCoin,
    sellerGstCoin,
    sellerTotalDeductedCoin,
    buyerPlatformFeeCoin,
    buyerGstCoin,
    buyerNetReceiveCoin,
  };
}

function getDisplayName(user) {
  if (!user) return 'User';
  if (user.username) return user.username;
  if (user.firstName || user.lastName) return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return 'User';
}

/* ---------------- Feedback / Reputation Summary helpers ---------------- */

function safeLower(s) {
  return String(s || '').trim().toLowerCase();
}

function computeSummaryFromList(list) {
  const s = { positive: 0, neutral: 0, negative: 0 };
  for (const f of list || []) {
    const t = safeLower(f?.type || f?.rating);
    if (t === 'positive' || t === 'pos' || t === 'good') s.positive += 1;
    else if (t === 'negative' || t === 'neg' || t === 'bad') s.negative += 1;
    else s.neutral += 1;
  }
  return s;
}

function normalizeSummary(summary) {
  const pos = Number(summary?.positive || 0);
  const neu = Number(summary?.neutral || 0);
  const neg = Number(summary?.negative || 0);
  const total = Number.isFinite(Number(summary?.total))
    ? Number(summary.total)
    : (pos + neu + neg);

  const posRate = Number.isFinite(Number(summary?.posRate))
    ? Number(summary.posRate)
    : (total > 0 ? Math.round((pos / total) * 100) : 0);

  // optional extras (won't break if missing)
  const internalTotal = Number(summary?.internalTotal || 0);
  const externalTotal = Number(summary?.externalTotal || 0);

  return { positive: pos, neutral: neu, negative: neg, total, posRate, internalTotal, externalTotal };
}

/**
 * ✅ UPDATED: No extra API calls. We rely on cache returned with ads:
 * ad.user.reputation = { total, internalTotal, externalTotal, posRate? }
 */
function summaryFromAdReputation(ad) {
  const rep = ad?.user?.reputation || ad?.sellerP2P?.reputation || ad?.p2pProfile?.reputation || null;

  return normalizeSummary({
    // counts are not available in cache schema, so keep them 0 (UI uses total + posRate)
    positive: 0,
    neutral: 0,
    negative: 0,

    total: Number(rep?.total || 0),
    internalTotal: Number(rep?.internalTotal || 0),
    externalTotal: Number(rep?.externalTotal || 0),
    posRate: Number(rep?.posRate || 0), // optional backend field (safe if missing)
  });
}

function FeedbackPill({ value, title }) {
  return (
    <span
      title={title}
      className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-0.5 text-[10px] md:text-xs text-slate-700 dark:text-slate-200 tabular-nums"
    >
      {value}
    </span>
  );
}

export default function MarketList({ intent = 'sell', coin = 'USDT' }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedAdId, setExpandedAdId] = useState(null);
  const [amountInr, setAmountInr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // reputation/feedback summary per username (NOW built from ads cache, no API calls)
  const [feedbackMap, setFeedbackMap] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAds = async () => {
      setLoading(true);
      try {
        // If you want to BUY, you view SELL ads. If you want to SELL, you view BUY ads.
        const queryType = intent === 'buy' ? 'sell' : 'buy';
        const res = await p2pAxios.get(`/ads/list/${queryType}?coin=${coin}`);
        setAds(res.data.ads || []);
      } catch (err) {
        console.error('Failed to fetch ads:', err);
      } finally {
        setLoading(false);
      }
    };
    setExpandedAdId(null);
    setAmountInr('');
    fetchAds();
  }, [intent, coin]);

  // Collect usernames from ads
  const usernamesInAds = useMemo(() => {
    const set = new Set();
    for (const ad of ads || []) {
      const u = ad?.user?.username;
      if (u) set.add(String(u));
    }
    return Array.from(set);
  }, [ads]);

  // ✅ UPDATED: Build feedbackMap from ads cache (no network calls)
  useEffect(() => {
    const next = {};

    for (const ad of ads || []) {
      const uname = ad?.user?.username;
      if (!uname) continue;

      const key = safeLower(uname);
      if (!key) continue;

      // only set once per username
      if (next[key]) continue;

      next[key] = summaryFromAdReputation(ad);
    }

    setFeedbackMap(next);
  }, [ads, usernamesInAds]);

  const toggleExpand = (adId) => {
    setExpandedAdId((prev) => (prev === adId ? null : adId));
    setAmountInr('');
  };

  const paymentLabel = (pm) => {
    if (!pm) return '—';
    const arr = Array.isArray(pm) ? pm : [pm];
    if (arr.length <= 1) return arr[0] || '—';
    return `${arr[0]} +${arr.length - 1}`;
  };

  const startTrade = async (ad) => {
    const fiat = parseFloat(amountInr);
    if (isNaN(fiat) || fiat <= 0) return alert('Please enter a valid INR amount.');
    if (fiat < ad.minLimit || fiat > ad.maxLimit) {
      return alert(`Amount must be between ₹${fmtINR(ad.minLimit)} and ₹${fmtINR(ad.maxLimit)}.`);
    }

    setSubmitting(true);
    try {
      const chosenMethod =
        (Array.isArray(ad.paymentMethods) && ad.paymentMethods[0]) || 'bank';

      const res = await p2pAxios.post('/trades/start', {
        adId: ad._id,
        fiatAmount: fiat,
        paymentMethod: chosenMethod,
      });

      if (res.data.success) navigate(`/p2p/trade/${res.data.trade._id}`);
      else alert(res.data.message || 'Failed to start trade.');
    } catch (err) {
      console.error('Failed to start trade:', err.response?.data || err.message);
      alert(err?.response?.data?.message || 'Error starting trade');
    } finally {
      setSubmitting(false);
    }
  };

  const quickAmount = (type, ad) => {
    if (!ad) return;
    const { minLimit, maxLimit } = ad;
    let v = minLimit;
    if (type === '25') v = maxLimit * 0.25;
    if (type === '50') v = maxLimit * 0.5;
    if (type === '75') v = maxLimit * 0.75;
    if (type === '100') v = maxLimit;
    v = clamp(v, minLimit, maxLimit);
    setAmountInr(String(r2(v)));
  };

  return (
    <>
      {loading ? (
        <div className="py-6 md:py-10 space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="relative overflow-hidden bg-white dark:bg-slate-900 rounded-2xl shadow-md p-3 md:p-5 border border-gray-200 dark:border-slate-700 animate-pulse"
            >
              <div className="absolute inset-x-0 top-0 h-1.5 bg-blue-600" />
              <div className="h-4 w-1/3 bg-gray-200 dark:bg-slate-700 rounded mb-2" />
              <div className="h-3 w-2/3 bg-gray-200 dark:bg-slate-700 rounded mb-2" />
              <div className="h-3 w-1/2 bg-gray-200 dark:bg-slate-700 rounded" />
            </div>
          ))}
        </div>
      ) : ads.length === 0 ? (
        <div className="text-center text-gray-500 dark:text-gray-400 py-10">
          No active ads found.
          <div>
            <button
              className="mt-3 inline-flex items-center px-3 py-1.5 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 md:gap-6 -mx-4 md:mx-0">
          {ads.map((ad) => {
            const isExpanded = expandedAdId === ad._id;
            const symbol = (ad.coin || coin || 'USDT').toUpperCase();
            const dp = COIN_DECIMALS[symbol] ?? 6;
            const isSellerCurrentUser = intent === 'sell';
            const expireMins = parseExpireMinutes(ad?.expireMinutes);

            const breakdown = isExpanded
              ? computeBreakdown(parseFloat(amountInr) || 0, ad.price, isSellerCurrentUser, symbol)
              : null;

            const uname = ad?.user?.username || '';
            const fb = feedbackMap[safeLower(uname)] || null;

            const presence = ad?.user?.presence || {};
            const presenceLabel = getPresenceLabel(presence);
            const presenceStyle = getPresenceStyle(presence);
            const isOnline = !!presence?.online || String(presence?.status || '').toLowerCase() === 'online';
            const lastSeenAt = presence?.lastSeenAt;

            return (
              <div
                key={ad._id}
                className="relative overflow-hidden bg-white dark:bg-slate-900 rounded-2xl shadow-md p-2.5 md:p-5 border border-gray-200 dark:border-slate-700"
              >
                <div className="absolute inset-x-0 top-0 h-1.5 bg-blue-600" />

                {/* Header row */}
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] md:items-center gap-1.5 md:gap-4">
                  <div className="space-y-1 md:space-y-2">
                    {/* Trader line */}
                    <p className="flex flex-wrap items-baseline gap-1.5 text-[12px] md:text-lg font-medium text-gray-700 dark:text-gray-200 leading-tight">
                      <span className="uppercase tracking-wide text-[10px] md:text-sm text-gray-500 dark:text-gray-400">
                        {ad.type === 'sell' ? 'Seller' : 'Buyer'}:
                      </span>

                      {ad.user?.username ? (
                        <Link
                          to={`/p2p/user/${encodeURIComponent(ad.user.username)}`}
                          className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline max-w-full md:max-w-none truncate"
                          title={`@${ad.user.username}`}
                        >
                          @{ad.user.username}
                        </Link>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">{getDisplayName(ad.user)}</span>
                      )}

                      {/* ✅ Combined reputation pills (NOW from ads cache, no API calls) */}
                      {ad.user?.username && (
                        <span className="ml-1 inline-flex items-center gap-1.5">
                          <FeedbackPill
                            title="Total reputation (Coinzetto + verified external)"
                            value={`⭐ ${fb ? fb.total : 0}`}
                          />
                          <FeedbackPill
                            title="Positive rate (if backend provides posRate in cache)"
                            value={`👍 ${fb ? fb.posRate : 0}%`}
                          />

                          {/* 🟢 Presence badge */}
                          <span
                            title={isOnline ? "User is online" : formatLastSeen(lastSeenAt)}
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] md:text-xs border ${presenceStyle.wrapper}`}
                          >
                            <span
                              className={`inline-block w-1.5 h-1.5 rounded-full ${presenceStyle.dot}`}
                            />
                            {presenceLabel}
                          </span>
                        </span>
                      )}
                    </p>

                    {/* Desktop meta */}
                    <div className="hidden md:block text-sm text-gray-600 dark:text-gray-300 space-y-1 font-normal">
                      <p className="tabular-nums">
                        Price:{' '}
                        <span className="font-semibold text-green-600 dark:text-green-400">₹{fmtINR(ad.price)}</span> per {symbol}
                      </p>
                      <p className="tabular-nums">
                        Limits: ₹{fmtINR(ad.minLimit)} - ₹{fmtINR(ad.maxLimit)}
                      </p>
                      <p>
                        Payment:{' '}
                        <span className="text-gray-800 dark:text-gray-200">
                          {(ad.paymentMethods || []).join(', ') || '—'}
                        </span>
                      </p>
                    </div>

                    {/* Mobile compact meta */}
                    <div className="md:hidden text-[11px] text-gray-600 dark:text-gray-300 flex flex-wrap items-center gap-x-2 gap-y-0.5 leading-4">
                      <span className="font-semibold text-green-700 dark:text-green-400 tabular-nums">
                        ₹{fmtINR(ad.price)}
                      </span>
                      <span>/{symbol}</span>
                      <span className="text-gray-400 dark:text-gray-500">•</span>
                      <span className="tabular-nums">
                        ₹{fmtINR(ad.minLimit)}–₹{fmtINR(ad.maxLimit)}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500">•</span>
                      <span className="text-gray-800 dark:text-gray-200 truncate">
                        {paymentLabel(ad.paymentMethods)}
                      </span>
                    </div>

                    {/* Time limit chip */}
                    <div className="pt-0.5">
                      <span className="text-[10px] md:text-sm text-gray-600 dark:text-gray-400 align-middle">Trade time limit: </span>
                      <span className="inline-flex items-center rounded-full bg-indigo-50 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 text-[10px] md:text-xs font-medium align-middle">
                        {fmtExpiry(expireMins)}
                      </span>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="w-full md:w-auto md:justify-self-end">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        aria-controls={`ad-panel-${ad._id}`}
                        onClick={() => toggleExpand(ad._id)}
                        className="bg-indigo-600 text-white px-2.5 py-1 text-[11px] rounded-md hover:bg-indigo-700 transition md:px-6 md:py-2 md:text-sm md:rounded-xl"
                      >
                        {intent === 'buy' ? `Buy ${symbol}` : `Sell ${symbol}`}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded panel */}
                {isExpanded && (
                  <div id={`ad-panel-${ad._id}`} className="mt-3 md:mt-5 border-t border-gray-200 dark:border-slate-700 pt-3 md:pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] md:items-end gap-2.5 md:gap-3">
                      {/* Amount input + quick chips */}
                      <div className="flex-1 w-full">
                        <label className="block text-[11px] md:text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                          Enter amount (INR)
                        </label>
                        <div className="flex">
                          <input
                            type="number"
                            min={ad.minLimit}
                            max={ad.maxLimit}
                            value={amountInr}
                            onChange={(e) => setAmountInr(e.target.value)}
                            placeholder={`₹ between ${fmtINR(ad.minLimit)} and ${fmtINR(ad.maxLimit)}`}
                            className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 rounded-md px-2.5 py-1.5 text-[12px] md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <QuickChip onClick={() => setAmountInr(String(r2(ad.minLimit)))}>Min</QuickChip>
                          <QuickChip onClick={() => quickAmount('25', ad)}>25%</QuickChip>
                          <QuickChip onClick={() => quickAmount('50', ad)}>50%</QuickChip>
                          <QuickChip onClick={() => quickAmount('75', ad)}>75%</QuickChip>
                          <QuickChip onClick={() => quickAmount('100', ad)}>Max</QuickChip>
                        </div>
                        <p className="mt-1 text-[10px] md:text-xs text-gray-500 dark:text-gray-400">
                          This trade will expire in <strong>{fmtExpiry(expireMins)}</strong> if the buyer doesn’t mark as paid.
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-1.5 md:gap-2 justify-end">
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => startTrade(ad)}
                          className="bg-green-600 disabled:opacity-50 text-white px-2.5 py-1 text-[11px] rounded-md hover:bg-green-700 transition md:px-4 md:py-2 md:text-sm"
                        >
                          {submitting ? 'Starting…' : (intent === 'buy' ? `Buy ${symbol}` : `Sell ${symbol}`)}
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleExpand(ad._id)}
                          className="bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-200 px-2.5 py-1 text-[11px] rounded-md hover:bg-gray-300 dark:hover:bg-slate-600 transition md:px-4 md:py-2 md:text-sm"
                        >
                          Close
                        </button>
                      </div>
                    </div>

                    {/* Fees & Taxes */}
                    {breakdown && (
                      <div className="mt-3 md:mt-6 p-2.5 md:p-4 rounded-lg bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
                        <h4 className="text-[12px] md:text-lg font-semibold text-blue-700 dark:text-blue-300 mb-2 leading-tight">
                          Fees &amp; Taxes
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 text-[11px] md:text-sm text-gray-800 dark:text-gray-200">
                          <div className="space-y-1">
                            <p className="tabular-nums">
                              <strong>Total Trade Amount (INR):</strong> ₹{fmtINR(+amountInr || 0)}
                            </p>

                            {isSellerCurrentUser && (
                              <>
                                <p className="tabular-nums">
                                  <strong>TDS (1%):</strong> ₹{fmtINR(breakdown.tdsInr)}
                                </p>
                                <p className="tabular-nums">
                                  <strong>Net Credit in Bank (INR):</strong> ₹{fmtINR(breakdown.payableFiatInr)}
                                </p>
                              </>
                            )}

                            <p className="tabular-nums">
                              <strong>Gross {symbol} (before fees):</strong> {fmtCoin(breakdown.grossCoin, dp)} {symbol}
                            </p>
                          </div>

                          <div className="space-y-1">
                            {isSellerCurrentUser ? (
                              <>
                                <p className="tabular-nums">
                                  <strong>Platform Fee (0.5%) — Seller:</strong>{' '}
                                  {fmtCoin(breakdown.sellerPlatformFeeCoin, dp)} {symbol}
                                </p>
                                <p className="tabular-nums">
                                  <strong>GST on Fee (18%) — Seller:</strong>{' '}
                                  {fmtCoin(breakdown.sellerGstCoin, dp)} {symbol}
                                </p>
                                <p className="tabular-nums">
                                  <strong>Escrowed from Seller (at start):</strong>{' '}
                                  {fmtCoin(breakdown.sellerTotalDeductedCoin, dp)} {symbol}
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="tabular-nums">
                                  <strong>Platform Fee (0.5%) — Buyer:</strong>{' '}
                                  {fmtCoin(breakdown.buyerPlatformFeeCoin, dp)} {symbol}
                                </p>
                                <p className="tabular-nums">
                                  <strong>GST on Fee (18%) — Buyer:</strong>{' '}
                                  {fmtCoin(breakdown.buyerGstCoin, dp)} {symbol}
                                </p>
                                <p className="tabular-nums">
                                  <strong>Buyer Receives After Fees:</strong>{' '}
                                  {fmtCoin(breakdown.buyerNetReceiveCoin, dp)} {symbol}
                                </p>
                              </>
                            )}
                          </div>
                        </div>

                        <p className="mt-1.5 text-[10px] md:text-xs text-gray-500 dark:text-gray-400">
                          * Platform fee is 0.5% and GST is 18% of the fee. TDS (1%) applies only when <strong>you</strong> are the seller.
                          Buyers pay only platform fee + GST (in {symbol}) at release.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/* ---------- tiny UI bits ---------- */

function QuickChip({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2 py-0.5 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-[10px] md:text-xs text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-slate-700 transition"
    >
      {children}
    </button>
  );
}
